import {
  Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-mapped-report-pdf-pane',
  templateUrl: './mapped-report-pdf-pane.component.html',
  styleUrls: ['./mapped-report-pdf-pane.component.scss'],
  standalone: false,
})
export class MappedReportPdfPaneComponent implements OnDestroy {
  // PREVIEW-ONLY override appended to the iframe srcdoc so the form fills the
  // pane edge-to-edge with no surrounding whitespace. The server HTML body uses
  // `max-width:210mm; padding:15mm` (print margins) which leaves a large margin
  // on screen. This style applies ONLY to the iframe preview — the raw
  // `detail.html` (used by the header Download PDF) is untouched, so the
  // generated PDF keeps its 15mm print margins.
  private static readonly PREVIEW_STYLE =
    '<style>html,body{margin:0!important;padding:0!important;max-width:none!important;width:auto!important;}</style>';

  // Fully rendered mapped-report HTML for THIS instance (from /v1/mapped-report detail).
  @Input()
  set html(value: string) {
    this._html = value || '';
    // Trusted, server-rendered document from our own backend; bound via [srcdoc].
    // Append the preview override LAST so it wins over the inline body styles.
    this.safeHtml = this._html
      ? this.sanitizer.bypassSecurityTrustHtml(this._html + MappedReportPdfPaneComponent.PREVIEW_STYLE)
      : null;
  }
  get html(): string {
    return this._html;
  }

  // The form is a fixed (~square) aspect document; emit its rendered pixel
  // height so the viewer can size both panes to it — removing the dead space
  // below the form and keeping the map the same height.
  @Output() formHeight = new EventEmitter<number>();

  @ViewChild('frame') frameRef?: ElementRef<HTMLIFrameElement>;

  safeHtml: SafeHtml | null = null;

  private _html = '';
  private resizeObs?: ResizeObserver;
  private lastWidth = -1;
  private lastEmitted = -1;

  constructor(private sanitizer: DomSanitizer, private host: ElementRef<HTMLElement>) {}

  // The iframe srcdoc finished loading — its document is now measurable.
  onFrameLoad(): void {
    this.measure();
    // Re-measure when the pane WIDTH changes (splitter drag / window resize):
    // the form reflows and its height changes. Guarding on width keeps setting
    // the height (which the viewer does from our emit) from feeding back into a
    // measure loop.
    if (!this.resizeObs && typeof ResizeObserver !== 'undefined') {
      this.lastWidth = this.host.nativeElement.clientWidth;
      this.resizeObs = new ResizeObserver(() => {
        const w = this.host.nativeElement.clientWidth;
        if (w !== this.lastWidth) {
          this.lastWidth = w;
          this.measure();
        }
      });
      this.resizeObs.observe(this.host.nativeElement);
    }
  }

  private measure(): void {
    const doc = this.frameRef?.nativeElement?.contentDocument;
    if (!doc?.body) return;
    const h = Math.ceil(doc.body.scrollHeight);
    if (h > 0 && h !== this.lastEmitted) {
      this.lastEmitted = h;
      this.formHeight.emit(h);
    }
  }

  ngOnDestroy(): void {
    this.resizeObs?.disconnect();
  }
}
